import type { AnyInputEvent, Binding, BindingCondition, InputModifiers, Point } from './types';

const TAP_MAX_MOVE_PX = 8;
const TAP_MAX_VEL = 0.5;
const VELOCITY_ALPHA = 0.4;
const INERTIA_FRICTION = 0.97;
const INERTIA_MIN_VEL = 0.5;

export class InputManager {
    // ── Raw event bus ────────────────────────────────────────────────────────
    private handlers = new Set<(e: AnyInputEvent) => void>();

    // ── Action registry ──────────────────────────────────────────────────────
    private actions = new Map<string, () => void>();

    // ── Binding table ────────────────────────────────────────────────────────
    private bindings: Binding[] = [];

    // ── Focus stack ──────────────────────────────────────────────────────────
    private focusStack: string[] = [];

    // ── Touch state ──────────────────────────────────────────────────────────
    private touchPhase: 'idle' | 'scroll' | 'pinch' = 'idle';
    private touchStartTarget: Element | null = null;
    private touchStartX = 0;
    private touchStartY = 0;
    private touchLastX = 0;
    private touchLastY = 0;
    private touchVelX = 0;
    private touchVelY = 0;
    private pinchStartDist = 0;
    private pinchLastScale = 1;

    // ── Scroll inertia state ─────────────────────────────────────────────────
    private scrollInertiaRafId: number | null = null;
    private scrollInertiaVelX = 0;
    private scrollInertiaVelY = 0;
    private scrollInertiaSource: 'touch' | 'touchpad' = 'touch';
    private scrollInertiaTarget: Element | null = null;
    private scrollInertiaLastX = 0;
    private scrollInertiaLastY = 0;

    // ── Current DOM event (set during synchronous dispatch, for preventDefault) ─
    private currentDomEvent: Event | null = null;

    // ── Mouse drag state ─────────────────────────────────────────────────────
    private dragActive = false;
    private dragButton = 0;
    private dragStartTarget: Element | null = null;
    private dragLastX = 0;
    private dragLastY = 0;

    // ── Public API ───────────────────────────────────────────────────────────

    registerAction(componentName: string, actionName: string, callback: () => void): () => void {
        const id = `${componentName}:${actionName}`;
        this.actions.set(id, callback);
        return () => this.actions.delete(id);
    }

    setBindings(bindings: Binding[]): void {
        this.bindings = bindings;
    }

    subscribe(handler: (e: AnyInputEvent) => boolean | void): () => void {
        this.handlers.add(handler);
        return () => this.handlers.delete(handler);
    }

    // ── Focus stack ──────────────────────────────────────────────────────────

    private rebuildStackFromElement(el: Element | null): string[] {
        const stack: string[] = [];
        let node: Element | null = el;
        while (node) {
            const name = node.getAttribute('data-input-component');
            if (name) stack.unshift(name);
            node = node.parentElement;
        }
        return stack;
    }

    private updateFocusStack(el: Element | null): void {
        const stack = this.rebuildStackFromElement(el);
        if (stack.length > 0) this.focusStack = stack;
        // keep last known if walk finds no marked ancestors
    }

    // ── Binding dispatch ─────────────────────────────────────────────────────

    private matchesCondition(cond: BindingCondition, event: AnyInputEvent): boolean {
        if (cond.eventType !== event.type) return false;
        if (cond.chord) {
            if (event.type !== 'key' || event.chord !== cond.chord) return false;
        }
        return true;
    }

    private dispatchToBindings(event: AnyInputEvent, stack: string[]): boolean {
        // Walk top (innermost) to bottom (outermost) — first level with a match wins,
        // but all bindings at that level fire (supports multiple actions per key).
        for (let i = stack.length - 1; i >= 0; i--) {
            const matched = this.bindings.filter(
                b => b.condition.focusedComponent === stack[i] && this.matchesCondition(b.condition, event)
            );
            if (matched.length > 0) {
                matched.forEach(b => this.actions.get(b.actionId)?.());
                return true;
            }
        }
        // Fall back to global bindings — all matching globals fire.
        const globals = this.bindings.filter(b => !b.condition.focusedComponent && this.matchesCondition(b.condition, event));
        if (globals.length > 0) {
            globals.forEach(b => this.actions.get(b.actionId)?.());
            return true;
        }
        return false;
    }

    private emit(event: AnyInputEvent): void {
        let claimed = false;
        this.handlers.forEach(h => { if (h(event) === true) claimed = true; });
        if (claimed && this.currentDomEvent) {
            this.currentDomEvent.preventDefault();
            this.currentDomEvent.stopImmediatePropagation();
        }
    }

    // ── Scroll inertia ───────────────────────────────────────────────────────

    private startScrollInertia(velX: number, velY: number, source: 'touch' | 'touchpad', target: Element | null, lastX: number, lastY: number): void {
        this.stopScrollInertia();
        this.scrollInertiaVelX = velX;
        this.scrollInertiaVelY = velY;
        this.scrollInertiaSource = source;
        this.scrollInertiaTarget = target;
        this.scrollInertiaLastX = lastX;
        this.scrollInertiaLastY = lastY;
        this.scrollInertiaRafId = requestAnimationFrame(this.scrollInertiaStep);
    }

    private stopScrollInertia(): void {
        if (this.scrollInertiaRafId !== null) {
            cancelAnimationFrame(this.scrollInertiaRafId);
            this.scrollInertiaRafId = null;
        }
    }

    private scrollInertiaStep = (): void => {
        this.scrollInertiaVelX *= INERTIA_FRICTION;
        this.scrollInertiaVelY *= INERTIA_FRICTION;
        if (Math.abs(this.scrollInertiaVelX) < INERTIA_MIN_VEL && Math.abs(this.scrollInertiaVelY) < INERTIA_MIN_VEL) {
            this.scrollInertiaRafId = null;
            return;
        }
        this.emit({
            type: 'scroll',
            dx: this.scrollInertiaVelX,
            dy: this.scrollInertiaVelY,
            velocityX: this.scrollInertiaVelX,
            velocityY: this.scrollInertiaVelY,
            phase: 'change',
            position: { x: this.scrollInertiaLastX, y: this.scrollInertiaLastY },
            startTarget: this.scrollInertiaTarget,
            source: this.scrollInertiaSource,
            inertia: true,
        });
        this.scrollInertiaRafId = requestAnimationFrame(this.scrollInertiaStep);
    };

    // ── Helpers ──────────────────────────────────────────────────────────────

    // Touchpad events use pixel mode with small continuous deltas.
    // A physical mouse wheel click produces large discrete deltas or non-pixel deltaMode.
    private isTouchpadEvent(e: WheelEvent): boolean {
        if (e.deltaMode !== 0) return false;
        if (Math.abs(e.deltaY) >= 50 && Math.abs(e.deltaX) < 1) return false;
        return true;
    }

    private wheelDeltaToPx(delta: number, deltaMode: number): number {
        if (deltaMode === 1 /* DOM_DELTA_LINE */) return delta * 20;
        if (deltaMode === 2 /* DOM_DELTA_PAGE */) return delta * window.innerHeight;
        return delta;
    }

    private modifiers(e: MouseEvent | KeyboardEvent | WheelEvent): InputModifiers {
        return { ctrl: e.ctrlKey, meta: e.metaKey, shift: e.shiftKey, alt: e.altKey };
    }

    private pinchDist(touches: TouchList): number {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
    }

    private pinchCenter(touches: TouchList): Point {
        return {
            x: (touches[0].clientX + touches[1].clientX) / 2,
            y: (touches[0].clientY + touches[1].clientY) / 2,
        };
    }

    private buildChord(e: KeyboardEvent): string {
        const parts: string[] = [];
        if (e.ctrlKey) parts.push('ctrl');
        if (e.metaKey) parts.push('meta');
        if (e.shiftKey) parts.push('shift');
        if (e.altKey) parts.push('alt');
        const key = /^Key[A-Z]$/.test(e.code) ? e.code.slice(3).toLowerCase()
                  : /^Digit[0-9]$/.test(e.code) ? e.code.slice(5)
                  : e.key.toLowerCase();
        parts.push(key);
        return parts.join('+');
    }

    // ── Focus listeners ──────────────────────────────────────────────────────

    private onPointerDown = (e: PointerEvent) => {
        // Touch focus is handled exclusively in onTouchEnd (tap detection).
        // Allowing touch here would shift keyboard focus on every scroll start.
        if (e.pointerType === 'touch') return;
        this.updateFocusStack(e.target instanceof Element ? e.target : null);
    };

    private onFocusIn = (e: FocusEvent) => {
        // covers tab navigation and programmatic .focus() calls
        this.updateFocusStack(e.target instanceof Element ? e.target : null);
    };

    private hasSuppressTapClick(el: Element | null): boolean {
        let node: Element | null = el;
        while (node) {
            if (node.hasAttribute('data-suppress-tap-click')) return true;
            node = node.parentElement;
        }
        return false;
    }

    private onFocusOut = (e: FocusEvent) => {
        if (!e.relatedTarget) this.focusStack = [];
    };

    // ── Input event handlers ─────────────────────────────────────────────────

    private onKeyDown = (e: KeyboardEvent) => {
        if (['Control', 'Meta', 'Shift', 'Alt'].includes(e.key)) return;
        const event: AnyInputEvent = {
            type: 'key',
            chord: this.buildChord(e),
            modifiers: this.modifiers(e),
            originalEvent: e,
        };
        this.emit(event);
        if (this.dispatchToBindings(event, this.focusStack)) e.preventDefault();
    };

    private onWheel = (e: WheelEvent) => {
        const target = e.target instanceof Element ? e.target : null;
        this.currentDomEvent = e;
        if (e.ctrlKey) {
            e.preventDefault();
            const event: AnyInputEvent = {
                type: 'ctrl_wheel',
                deltaY: e.deltaY,
                position: { x: e.clientX, y: e.clientY },
                startTarget: target,
                originalEvent: e,
            };
            this.emit(event);
            const localStack = this.rebuildStackFromElement(target);
            this.dispatchToBindings(event, localStack.length ? localStack : this.focusStack);
        } else {
            const dx = this.wheelDeltaToPx(e.deltaX, e.deltaMode);
            const dy = this.wheelDeltaToPx(e.deltaY, e.deltaMode);
            const isTouchpad = this.isTouchpadEvent(e);
            if (isTouchpad) {
                this.stopScrollInertia();
            }
            const event: AnyInputEvent = {
                type: 'scroll',
                dx, dy,
                velocityX: dx,
                velocityY: dy,
                phase: 'change',
                position: { x: e.clientX, y: e.clientY },
                startTarget: target,
                source: isTouchpad ? 'touchpad' : 'wheel',
            };
            this.emit(event);
        }
        this.currentDomEvent = null;
    };

    private onTouchStart = (e: TouchEvent) => {
        if (e.touches.length === 1) {
            this.stopScrollInertia();
            const t = e.changedTouches[0];
            this.touchPhase = 'scroll';
            this.touchStartTarget = t.target instanceof Element ? t.target : null;
            this.touchStartX = this.touchLastX = t.clientX;
            this.touchStartY = this.touchLastY = t.clientY;
            this.touchVelX = 0;
            this.touchVelY = 0;
            const event: AnyInputEvent = {
                type: 'scroll',
                dx: 0, dy: 0,
                velocityX: 0, velocityY: 0,
                phase: 'start',
                position: { x: t.clientX, y: t.clientY },
                startTarget: this.touchStartTarget,
                source: 'touch',
            };
            this.emit(event);
            const localStack = this.rebuildStackFromElement(this.touchStartTarget);
            this.dispatchToBindings(event, localStack.length ? localStack : this.focusStack);
        } else if (e.touches.length === 2) {
            if (this.touchPhase === 'scroll') {
                this.stopScrollInertia();
                this.emit({
                    type: 'scroll',
                    dx: 0, dy: 0,
                    velocityX: 0, velocityY: 0,
                    phase: 'cancel',
                    position: { x: this.touchLastX, y: this.touchLastY },
                    startTarget: this.touchStartTarget,
                    source: 'touch',
                });
            }
            this.touchPhase = 'pinch';
            this.pinchStartDist = this.pinchDist(e.touches);
            this.pinchLastScale = 1;
            const event: AnyInputEvent = {
                type: 'pinch',
                scale: 1,
                scaleDelta: 0,
                center: this.pinchCenter(e.touches),
                phase: 'start',
                startTarget: this.touchStartTarget,
            };
            this.emit(event);
            const localStack = this.rebuildStackFromElement(this.touchStartTarget);
            this.dispatchToBindings(event, localStack.length ? localStack : this.focusStack);
        }
    };

    private onTouchMove = (e: TouchEvent) => {
        if (e.touches.length === 1 && this.touchPhase === 'scroll') {
            const t = e.touches[0];
            const dx = this.touchLastX - t.clientX;
            const dy = this.touchLastY - t.clientY;
            this.touchVelX = this.touchVelX * (1 - VELOCITY_ALPHA) + dx * VELOCITY_ALPHA;
            this.touchVelY = this.touchVelY * (1 - VELOCITY_ALPHA) + dy * VELOCITY_ALPHA;
            this.touchLastX = t.clientX;
            this.touchLastY = t.clientY;
            const event: AnyInputEvent = {
                type: 'scroll',
                dx, dy,
                velocityX: this.touchVelX,
                velocityY: this.touchVelY,
                phase: 'change',
                position: { x: t.clientX, y: t.clientY },
                startTarget: this.touchStartTarget,
                source: 'touch',
            };
            this.emit(event);
            const localStack = this.rebuildStackFromElement(this.touchStartTarget);
            this.dispatchToBindings(event, localStack.length ? localStack : this.focusStack);
        } else if (e.touches.length === 2 && this.touchPhase === 'pinch') {
            const dist = this.pinchDist(e.touches);
            const scale = dist / this.pinchStartDist;
            const scaleDelta = scale - this.pinchLastScale;
            this.pinchLastScale = scale;
            const event: AnyInputEvent = {
                type: 'pinch',
                scale,
                scaleDelta,
                center: this.pinchCenter(e.touches),
                phase: 'change',
                startTarget: this.touchStartTarget,
            };
            this.emit(event);
            const localStack = this.rebuildStackFromElement(this.touchStartTarget);
            this.dispatchToBindings(event, localStack.length ? localStack : this.focusStack);
        }
    };

    private onTouchEnd = (e: TouchEvent) => {
        if (this.touchPhase === 'scroll' && e.touches.length === 0) {
            const isTap =
                Math.abs(this.touchVelX) < TAP_MAX_VEL &&
                Math.abs(this.touchVelY) < TAP_MAX_VEL &&
                Math.abs(this.touchLastX - this.touchStartX) < TAP_MAX_MOVE_PX &&
                Math.abs(this.touchLastY - this.touchStartY) < TAP_MAX_MOVE_PX;
            if (isTap) {
                if (this.hasSuppressTapClick(this.touchStartTarget)) e.preventDefault();
                // tap updates global focus stack
                this.updateFocusStack(this.touchStartTarget);
                const tapEvent: AnyInputEvent = {
                    type: 'tap',
                    position: { x: this.touchLastX, y: this.touchLastY },
                    startTarget: this.touchStartTarget,
                };
                this.emit(tapEvent);
                this.dispatchToBindings(tapEvent, this.focusStack);
            }
            const localStack = this.rebuildStackFromElement(this.touchStartTarget);
            const scrollEnd: AnyInputEvent = {
                type: 'scroll',
                dx: 0, dy: 0,
                velocityX: this.touchVelX,
                velocityY: this.touchVelY,
                phase: 'end',
                position: { x: this.touchLastX, y: this.touchLastY },
                startTarget: this.touchStartTarget,
                source: 'touch',
            };
            this.emit(scrollEnd);
            this.dispatchToBindings(scrollEnd, localStack.length ? localStack : this.focusStack);
            this.touchPhase = 'idle';
            if (Math.abs(this.touchVelX) > INERTIA_MIN_VEL || Math.abs(this.touchVelY) > INERTIA_MIN_VEL) {
                this.startScrollInertia(this.touchVelX, this.touchVelY, 'touch', this.touchStartTarget, this.touchLastX, this.touchLastY);
            }
        } else if (this.touchPhase === 'pinch' && e.touches.length < 2) {
            const event: AnyInputEvent = {
                type: 'pinch',
                scale: this.pinchLastScale,
                scaleDelta: 0,
                center: e.touches.length === 1
                    ? { x: e.touches[0].clientX, y: e.touches[0].clientY }
                    : { x: this.touchLastX, y: this.touchLastY },
                phase: 'end',
                startTarget: this.touchStartTarget,
            };
            this.emit(event);
            const localStack = this.rebuildStackFromElement(this.touchStartTarget);
            this.dispatchToBindings(event, localStack.length ? localStack : this.focusStack);
            this.touchPhase = 'idle';
        }
    };

    private onTouchCancel = () => {
        if (this.touchPhase === 'scroll') {
            this.stopScrollInertia();
            const event: AnyInputEvent = {
                type: 'scroll',
                dx: 0, dy: 0,
                velocityX: 0, velocityY: 0,
                phase: 'cancel',
                position: { x: this.touchLastX, y: this.touchLastY },
                startTarget: this.touchStartTarget,
                source: 'touch',
            };
            this.emit(event);
            const localStack = this.rebuildStackFromElement(this.touchStartTarget);
            this.dispatchToBindings(event, localStack.length ? localStack : this.focusStack);
        } else if (this.touchPhase === 'pinch') {
            const event: AnyInputEvent = {
                type: 'pinch',
                scale: this.pinchLastScale,
                scaleDelta: 0,
                center: { x: this.touchLastX, y: this.touchLastY },
                phase: 'cancel',
                startTarget: this.touchStartTarget,
            };
            this.emit(event);
            const localStack = this.rebuildStackFromElement(this.touchStartTarget);
            this.dispatchToBindings(event, localStack.length ? localStack : this.focusStack);
        }
        this.touchPhase = 'idle';
    };

    private onMouseDown = (e: MouseEvent) => {
        this.dragActive = true;
        this.dragButton = e.button;
        this.dragStartTarget = e.target instanceof Element ? e.target : null;
        this.dragLastX = e.clientX;
        this.dragLastY = e.clientY;
        const event: AnyInputEvent = {
            type: 'drag',
            dx: 0, dy: 0,
            position: { x: e.clientX, y: e.clientY },
            phase: 'start',
            button: e.button,
            startTarget: this.dragStartTarget,
        };
        this.emit(event);
        const localStack = this.rebuildStackFromElement(this.dragStartTarget);
        this.dispatchToBindings(event, localStack.length ? localStack : this.focusStack);
    };

    private onMouseMove = (e: MouseEvent) => {
        if (!this.dragActive) return;
        const dx = e.clientX - this.dragLastX;
        const dy = e.clientY - this.dragLastY;
        this.dragLastX = e.clientX;
        this.dragLastY = e.clientY;
        const event: AnyInputEvent = {
            type: 'drag',
            dx, dy,
            position: { x: e.clientX, y: e.clientY },
            phase: 'change',
            button: this.dragButton,
            startTarget: this.dragStartTarget,
        };
        this.emit(event);
        const localStack = this.rebuildStackFromElement(this.dragStartTarget);
        this.dispatchToBindings(event, localStack.length ? localStack : this.focusStack);
    };

    private onMouseUp = (e: MouseEvent) => {
        if (!this.dragActive) return;
        this.dragActive = false;
        const event: AnyInputEvent = {
            type: 'drag',
            dx: 0, dy: 0,
            position: { x: e.clientX, y: e.clientY },
            phase: 'end',
            button: this.dragButton,
            startTarget: this.dragStartTarget,
        };
        this.emit(event);
        const localStack = this.rebuildStackFromElement(this.dragStartTarget);
        this.dispatchToBindings(event, localStack.length ? localStack : this.focusStack);
    };

    // Attach all listeners to the document. Returns a cleanup function.
    attach(): () => void {
        document.addEventListener('keydown', this.onKeyDown);
        // capture: true to see wheel events before Monaco's internal stopPropagation
        document.addEventListener('wheel', this.onWheel, { capture: true, passive: false });
        document.addEventListener('touchstart', this.onTouchStart, { passive: true });
        document.addEventListener('touchmove', this.onTouchMove, { passive: true });
        document.addEventListener('touchend', this.onTouchEnd, { passive: false });
        document.addEventListener('touchcancel', this.onTouchCancel, { passive: true });
        document.addEventListener('mousedown', this.onMouseDown);
        document.addEventListener('mousemove', this.onMouseMove);
        document.addEventListener('mouseup', this.onMouseUp);
        document.addEventListener('pointerdown', this.onPointerDown);
        document.addEventListener('focusin', this.onFocusIn);
        document.addEventListener('focusout', this.onFocusOut);

        const observer = new MutationObserver(mutations => {
            for (const mutation of mutations) {
                for (const node of mutation.addedNodes) {
                    if (!(node instanceof Element)) continue;
                    if (node.getAttribute('data-input-focus') === 'move') {
                        this.updateFocusStack(node);
                    }
                    for (const child of node.querySelectorAll('[data-input-focus="move"]')) {
                        this.updateFocusStack(child);
                    }
                }
            }
        });
        observer.observe(document.body, { childList: true, subtree: true });

        return () => {
            document.removeEventListener('keydown', this.onKeyDown);
            document.removeEventListener('wheel', this.onWheel, { capture: true });
            document.removeEventListener('touchstart', this.onTouchStart);
            document.removeEventListener('touchmove', this.onTouchMove);
            document.removeEventListener('touchend', this.onTouchEnd);
            document.removeEventListener('touchcancel', this.onTouchCancel);
            document.removeEventListener('mousedown', this.onMouseDown);
            document.removeEventListener('mousemove', this.onMouseMove);
            document.removeEventListener('mouseup', this.onMouseUp);
            document.removeEventListener('pointerdown', this.onPointerDown);
            document.removeEventListener('focusin', this.onFocusIn);
            document.removeEventListener('focusout', this.onFocusOut);
            observer.disconnect();
            this.stopScrollInertia();
        };
    }
}
